import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, formatNumber } from "@/lib/utils";
import { Info, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { FC, ReactNode } from "react";

type ValueTag = "percent" | "count" | "rub" | "usd" | "none";

const TAGS: Record<ValueTag, string> = {
  none: "",
  percent: "%",
  count: "шт.",
  rub: "₽",
  usd: "$",
};

type Trend = "up" | "down" | "flat";

const TREND_ICON: Record<Trend, ReactNode> = {
  up: <TrendingUp size={20} className="text-green-500" aria-label="Рост" />,
  down: <TrendingDown size={20} className="text-red-500" aria-label="Падение" />,
  flat: <Minus size={20} className="text-muted-foreground" aria-label="Без изменений" />,
};

const TREND_COLOR: Record<Trend, string> = {
  up: "text-green-500",
  down: "text-red-500",
  flat: "text-muted-foreground",
};

const getTrend = (delta: number): Trend => (delta > 0 ? "up" : delta < 0 ? "down" : "flat");

interface NumeralDataWidgetProps {
  value: number;
  previousValue?: number;
  title: string;
  description?: string;
  valueTag?: ValueTag;
  hundredPercentValue?: number;
  className?: string;
}

const NumeralDataWidget: FC<NumeralDataWidgetProps> = ({
  value,
  previousValue,
  title,
  description,
  valueTag = "none",
  hundredPercentValue,
  className,
}) => {
  const suffix = TAGS[valueTag];

  const hasPrevious = previousValue !== undefined;
  const delta = hasPrevious ? value - previousValue : 0;
  const trend = hasPrevious ? getTrend(delta) : null;

  const percentage =
    hundredPercentValue !== undefined && hundredPercentValue !== 0
      ? (value * 100) / hundredPercentValue
      : null;

  const deltaLabel = `${delta > 0 ? "+" : ""}${formatNumber(delta)}${suffix}`;

  return (
    <Card
      className={cn("p-2 2xl:p-4 flex flex-col gap-1 2xl:gap-2 justify-center h-fit", className)}
    >
      <header className="flex items-center justify-between gap-x-2 text-xs 2xl:text-sm">
        <h3 className="font-semibold text-muted-foreground">{title}</h3>
        {suffix && <span className="text-xs 2xl:text-sm text-muted-foreground">{suffix}</span>}
      </header>

      <div className="flex items-center gap-x-1 text-sm 2xl:text-lg font-bold">
        <span>{formatNumber(value)}</span>
        {percentage !== null && (
          <span className="text-muted-foreground">({formatNumber(percentage)}%)</span>
        )}
        {suffix && <span className="text-muted-foreground">{suffix}</span>}
        {trend && TREND_ICON[trend]}
      </div>

      <footer className="flex items-center gap-x-1 text-sm text-muted-foreground">
        {hasPrevious && trend && (
          <>
            <span className={`font-semibold ${TREND_COLOR[trend]}`}>{deltaLabel}</span>
            <span>от прошлого периода</span>
          </>
        )}

        {description && (
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <Info
                size={16}
                className="ml-auto cursor-pointer hover:text-primary transition-all"
                aria-label="Подробнее"
              />
            </TooltipTrigger>
            <TooltipContent className="w-64">
              <p>{description}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </footer>
    </Card>
  );
};

export default NumeralDataWidget;
